/**
 * The L0 KPI strip at the top of the Dynamics board (dashboard design rules
 * §1): four numbers a reader would act on, in the order a reader asks them —
 * is anyone waiting, is the estate slow, is the platform throttling, did
 * anyone complain. Each tile is a door down to the section that explains it.
 *
 * ## Why four, and why not the six that were here before
 *
 * The strip used to open with synthetic availability, failed probe runs,
 * form-load volume and the environment count. A tile has one test to pass —
 * *what would I do differently if this number doubled?* — and those four do
 * not pass it:
 *
 * - **Synthetic availability** and **failed probe runs** are a control, not a
 *   verdict. The probe behind them is a URL ping that follows redirects and
 *   never signs in; over the measured week it ran 26,230 times and failed
 *   never, including in the hours users waited eight seconds for a form. Both
 *   numbers are kept, in full, in the board's control section at the bottom —
 *   framed as what the front door answered from outside, which is all they can
 *   ever say.
 * - **Form loads** is the base the latency figures are computed from, not a
 *   figure of its own. It is now stated in the caption of the tile it is the
 *   base for, so the P95 still cannot be read without its denominator.
 * - **Environments reporting** is an estate count. It moved to the control
 *   section, where it is the coverage beside the availability percentage —
 *   which is the honest job it was already doing (§5).
 *
 * That leaves no tile pointing at `#fo-requests` or `#fo-integration`. The note
 * this file used to carry treated that as forbidden and repointed two spare
 * tiles at those sections to keep the doors. It is the wrong trade: §4.1 says
 * every *aggregate* must be a door, not that every section must have an inbound
 * tile — and a tile whose number nobody acts on is exactly the dead weight a
 * strip exists to keep out. Both sections keep their headings, their own
 * `View all →` drills and their rail rows (`/dynamics/requests`,
 * `/dynamics/queues`).
 *
 * ## The honesty rules that do real work here
 *
 * - **`null` is neutral, never green and never zero.** Every counted field in
 *   `FoKpiSummary` and `FoConsoleSlowSummary` is nullable, and the distinction
 *   is the point: `0` means the query answered and counted nothing, `null`
 *   means the query that would have counted never answered. A null renders as
 *   an em dash with a caption saying which it is. "0 throttling events" in
 *   green and "we could not ask about throttling" are the two ends of the
 *   range, and the tone is the only thing telling them apart at a glance.
 * - **Coverage sits beside the number it qualifies** (§5). The slow-interaction
 *   count is complete; what is partial is the *network* half of its story, so
 *   the Cato coverage meter sits inside that tile with its own label and the
 *   standing DKTAA/CZPRI caveat, rather than floating as a tile of its own.
 * - **Thresholds are the report's, not this board's.** `FormP95MsParam` and
 *   `ThrottlingEventsParam` come down in the payload, so a tone shown here is
 *   the tone the Power BI report showed.
 *
 * ## Independent gating
 *
 * The strip reads three payloads that fail independently: `/fo/overview`,
 * `/fo/console/slow-summary`, and the report queue (which brings its own tile
 * and its own states, including a 403 for a reader who is not an investigator).
 * A payload that is not `ok` contributes **no tile at all** — its access panel
 * is rendered elsewhere on the board, and blank tiles would still imply the
 * numbers were looked for and found empty.
 */

import { Card, Statistic } from "@norican/react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import type { FoConsoleSlowSummary, FoKpiSummary } from "@/lib/api";
import type { StatusTone } from "@/lib/charts/config";

import { THROTTLING_ANCHOR } from "./availability-section";
import { CoverageMeter } from "./console-chips";
import { lookbackOption } from "./console-format";
import { EXPERIENCE_ANCHOR, SLOW_ANCHOR } from "./params";
import { formatCount, formatMs, formatWindow } from "./status";

interface KpiTileProps {
  /**
   * In-page anchor of the section that explains this number — a plain `<a>`, so
   * the browser scrolls without a route change.
   */
  href?: string;
  /**
   * A router route instead, for the one tile whose explanation lives on another
   * page (the report queue on `/dynamics/reports`). Exactly one of `href` and
   * `to` is given; a tile with neither would be a dead aggregate (§4.1).
   */
  to?: string;
  tone: StatusTone | "accent";
  children: ReactNode;
}

/** One KPI tile: a card that is also a link to its section (§4.1 — no dead aggregates). */
export function KpiTile({ href, to, tone, children }: KpiTileProps) {
  const card = (
    <Card variant="outline" accent="left" accentTone={tone} interactive className="h-full">
      <Card.Body>{children}</Card.Body>
    </Card>
  );
  if (to) {
    return (
      <Link to={to} className="block h-full rounded focus-visible:outline-2">
        {card}
      </Link>
    );
  }
  return (
    <a href={href} className="block h-full rounded focus-visible:outline-2">
      {card}
    </a>
  );
}

/**
 * Who is waiting, and how much of their story the network half can see.
 *
 * The count is what the console measured; the meter under it is the *Cato*
 * coverage, labelled so it reads as "the network path is visible for this many
 * of them" rather than as a doubt about the count itself. Its caption carries
 * the standing reason the ratio is low by construction (DKTAA and CZPRI are not
 * behind Cato yet).
 */
function SlowInteractionsTile({ summary }: { summary: FoConsoleSlowSummary }) {
  const option = lookbackOption(summary.lookback);
  // `total` is null when the counting query failed while the panel stayed ok —
  // unknown, which is neither zero nor a quiet lookback.
  const total = summary.total;
  const hasSlow = total != null && total > 0;

  return (
    <KpiTile href={`#${SLOW_ANCHOR}`} tone={hasSlow ? "warning" : "neutral"}>
      <div className="flex h-full flex-col gap-3">
        <Statistic
          label={`Slow interactions (${option.label.replace(" ", "")})`}
          value={formatCount(total)}
          tone={hasSlow ? "warning" : "neutral"}
          size="lg"
          caption={
            total == null
              ? `The slow-interaction count could not be read — unknown, not zero · ${option.caption}`
              : `${formatCount(summary.users)} users · ${formatCount(
                  summary.mapped,
                )} with an identity · ${option.caption} · over 10 s or 3× the form's 28-day median`
          }
        />
        <CoverageMeter coverage={summary.coverage} label="Network path visible for" size="sm" />
      </div>
    </KpiTile>
  );
}

/** The estate-wide experience verdict, with the base it was computed from. */
function FormP95Tile({ summary, windowLabel }: { summary: FoKpiSummary; windowLabel: string }) {
  const tone: StatusTone =
    summary.form_p95_ms == null
      ? "neutral"
      : summary.form_p95_ms > summary.form_p95_warning_ms
        ? "warning"
        : "success";

  // Three distinct absences, and they are not the same finding: the query
  // failed, the query answered and saw no form load, or the percentile is real
  // but its denominator went missing.
  const caption =
    summary.form_p95_ms == null
      ? summary.form_loads == null
        ? "Form performance could not be read — unknown is not fast"
        : "No form load was observed in this window — unknown is not fast"
      : `Warns above ${formatMs(summary.form_p95_warning_ms)} · ` +
        (summary.form_loads == null
          ? "the form-load base could not be counted, so this percentile has no known denominator"
          : `computed from ${formatCount(summary.form_loads)} form loads in the ${windowLabel}`);

  return (
    <KpiTile href={`#${EXPERIENCE_ANCHOR}`} tone={tone}>
      <Statistic
        label="Form load P95"
        value={formatMs(summary.form_p95_ms)}
        tone={tone}
        size="lg"
        caption={caption}
      />
    </KpiTile>
  );
}

/** Batch throttling — the platform fault the front door cannot see. */
function ThrottlingTile({ summary, windowLabel }: { summary: FoKpiSummary; windowLabel: string }) {
  const tone: StatusTone =
    summary.throttling_events == null
      ? "neutral"
      : summary.throttling_events > summary.throttling_warning_events
        ? "warning"
        : summary.throttling_events > 0
          ? "neutral"
          : "success";

  return (
    <KpiTile href={`#${THROTTLING_ANCHOR}`} tone={tone}>
      <Statistic
        label="Batch throttling events"
        value={formatCount(summary.throttling_events)}
        tone={tone}
        size="lg"
        caption={
          summary.throttling_events == null
            ? "Throttling could not be read — unknown, not zero"
            : `Warns above ${formatCount(
                summary.throttling_warning_events,
              )} in the ${windowLabel} · batch jobs queued behind the throttle, which no probe can see`
        }
      />
    </KpiTile>
  );
}

interface DynamicsKpiStripProps {
  /** `/fo/overview`'s KPIs, or `null` when that panel's `access_state` is not `ok`. */
  summary: FoKpiSummary | null;
  /** The overview's ISO 8601 window, stated on the tiles it scopes. */
  window: string;
  /**
   * `/fo/console/slow-summary`, or `null` when that panel's `access_state` is
   * not `ok`. Scoped by the board's `?lookback=`, which is why the lookback row
   * sits above this strip and not below it (§4.3).
   */
  slow: FoConsoleSlowSummary | null;
  /**
   * The report-queue tile, from a payload this component does not read —
   * `OpenReportsTile` in `reports-section.tsx`, which owns the queue's query
   * and its four states. Passed in rather than imported so the strip keeps no
   * dependency on the report layer.
   */
  reportsTile?: ReactNode;
}

export function DynamicsKpiStrip({ summary, window, slow, reportsTile }: DynamicsKpiStripProps) {
  const windowLabel = formatWindow(window);

  // Nothing readable at all: the board's access panels say why, and an empty
  // grid would just be a row of unexplained whitespace above them.
  if (!summary && !slow && !reportsTile) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {/* Is anyone waiting. */}
      {slow && <SlowInteractionsTile summary={slow} />}

      {/* Is the estate slow, and is the platform throttling. */}
      {summary && <FormP95Tile summary={summary} windowLabel={windowLabel} />}
      {summary && <ThrottlingTile summary={summary} windowLabel={windowLabel} />}

      {/* Did anyone complain. */}
      {reportsTile}
    </div>
  );
}
